import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type LotsList, type LotTrace } from '../api';
import Pager from '../components/Pager';
import { OriginLink, PickingPdfLink, ProductLink } from '../components/Links';

// Écran de traçabilité : un lot → d'où il vient, où il est, à qui il est parti.
// Sert en particulier au RAPPEL PRODUIT : sélectionner le lot, lire la liste des
// clients livrés, exporter leurs coordonnées.

function fmtQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
function fmtDate(d: string | null): string {
  if (!d) return '—';
  const [y, m, j] = d.split('-');
  return y && m && j ? `${j}.${m}.${y}` : d;
}
// Odoo préfixe display_name par « [code] » : on l'isole pour l'afficher en mono.
function splitCode(name: string): [string, string] {
  const m = name.match(/^\[([^\]]+)\]\s*(.*)$/);
  return m ? [m[1] ?? '', m[2] ?? ''] : ['', name];
}

const FILTERS: [string, string][] = [
  ['all', 'Tout'],
  ['en_stock', 'En stock'],
  ['bientot', 'Périment bientôt'],
  ['perime', 'Périmés en stock'],
];

/** Coordonnées des clients livrés, en CSV — la liste d'appel d'un rappel produit. */
function exportRappel(trace: LotTrace): void {
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = [
    ['Lot', 'Produit', 'Peremption', 'Client', 'Email', 'Telephone', 'Ville', 'Pays', 'Quantite livree', 'Dates de livraison'],
    ...trace.clients.map((c) => [
      trace.lot.name, trace.lot.product?.name ?? '', trace.lot.expiration ?? '',
      c.name, c.email ?? '', c.phone ?? '', c.city ?? '', c.country ?? '',
      fmtQty(c.qty), c.dates.join(' '),
    ]),
  ];
  const csv = '﻿' + rows.map((r) => r.map(esc).join(';')).join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `rappel-lot-${trace.lot.name.replace(/[^\w.-]+/g, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const PAGE_SIZE = 50;

export default function Lots() {
  const [data, setData] = useState<LotsList | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Recherche, filtre et lot ouvert vivent dans l'URL — c'est ce qui rend l'écran
  // atteignable depuis les autres : `?lot=<id>` (lien direct depuis un mouvement de
  // stock) ou `?q=<texte>` (tous les lots d'un produit, depuis sa fiche).
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const filter = params.get('filtre') ?? 'all';
  const selected = Number(params.get('lot')) || null;
  const [qInput, setQInput] = useState(q);
  useEffect(() => { setQInput(q); }, [q]);

  function patch(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p, { replace: true });
  }
  const setSelected = (id: number | null) => patch({ lot: id ? String(id) : null });

  const [trace, setTrace] = useState<LotTrace | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getLots({ q, filter, limit: PAGE_SIZE, offset })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q, filter, offset]);
  useEffect(() => { reload(); }, [reload]);

  // Recherche d'un lot précis (lien depuis un produit) : ouvrir sa traçabilité
  // d'emblée. Une seule fois par recherche — sinon refermer le panneau le
  // rouvrirait aussitôt, et le bouton « Fermer » paraîtrait inerte.
  const autoOpened = useRef<string | null>(null);
  useEffect(() => {
    if (!q || autoOpened.current === q || !data || data.items.length !== 1) return;
    autoOpened.current = q;
    if (selected == null) setSelected(data.items[0]!.id);
  }, [q, data]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selected == null) { setTrace(null); return; }
    setTraceLoading(true);
    setTraceError(null);
    api.getLot(selected)
      .then(setTrace)
      .catch((e) => setTraceError(e.message))
      .finally(() => setTraceLoading(false));
  }, [selected]);

  const c = data?.counts;

  return (
    <div className="devis-page">
      <div className="page-toolbar">
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>

      {c && (
        <div className="kpi-grid stock-kpis">
          <div className="kpi-card"><div className="kpi-label">Lots{c.capped ? ' (max)' : ''}</div><div className="kpi-value">{c.total}</div></div>
          <div className="kpi-card"><div className="kpi-label">En stock</div><div className="kpi-value">{c.en_stock}</div></div>
          <div className="kpi-card"><div className="kpi-label">Périment ≤ 60 j</div><div className="kpi-value">{c.bientot}</div></div>
          <div className="kpi-card kpi-danger"><div className="kpi-label">Périmés en stock</div><div className="kpi-value">{c.perime}</div></div>
        </div>
      )}

      <div className="ventes-toolbar">
        <div className="suivi-filters">
          {FILTERS.map(([k, lbl]) => (
            <button
              key={k}
              className={`suivi-filter ${filter === k ? 'active' : ''}`}
              onClick={() => { patch({ filtre: k === 'all' ? null : k, lot: null }); setOffset(0); }}
            >{lbl}</button>
          ))}
        </div>
        <form onSubmit={(e) => {
          e.preventDefault();
          patch({ q: qInput.trim() || null, lot: null });
          setOffset(0);
        }}>
          <input
            className="stock-search"
            placeholder="N° de lot, code ou nom de produit…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
          />
        </form>
      </div>

      <div className="devis-list stock-list">
        {loading && <div className="devis-empty">Chargement…</div>}
        {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
        {!loading && !error && data && data.items.length === 0 && <div className="devis-empty">Aucun lot.</div>}
        {!loading && !error && data && data.items.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Lot</th><th>Code</th><th className="prod">Produit</th>
                <th className="num">En stock</th><th>Péremption</th><th>Créé le</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((l) => {
                const [code, name] = splitCode(l.product?.name ?? '');
                return (
                  <tr key={l.id} className={selected === l.id ? 'row-selected' : ''}>
                    <td className="mono cell-title"><b>{l.name || '—'}</b></td>
                    <td className="mono" data-label="Code">
                      {code ? <ProductLink id={l.product?.id}>{code}</ProductLink> : '—'}
                    </td>
                    <td className="prod" data-label="Produit">
                      <ProductLink id={l.product?.id}><span title={name}>{name}</span></ProductLink>
                    </td>
                    <td className={`num ${l.qty <= 0 ? 'stock-short' : ''}`} data-label="En stock">{fmtQty(l.qty)}</td>
                    <td data-label="Péremption">
                      {fmtDate(l.expiration)}
                      {l.expired && l.qty > 0 && <span className="badge badge-danger" style={{ marginLeft: 6 }}>périmé</span>}
                      {l.expiring && l.qty > 0 && <span className="badge" style={{ marginLeft: 6 }}>≤ 60 j</span>}
                    </td>
                    <td className="sm-hide" data-label="Créé le">{fmtDate(l.created)}</td>
                    <td className="cell-actions">
                      <button className="devis-refresh" onClick={() => setSelected(selected === l.id ? null : l.id)}>
                        {selected === l.id ? 'Fermer' : 'Traçabilité'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && !error && data && (
          <Pager
            total={data.total} limit={data.limit} offset={data.offset}
            onOffset={(o) => { setOffset(o); setSelected(null); }}
            unit={['lot', 'lots']}
            capped={data.counts.capped}
            cappedNote="au-delà de 2000 lots, le reste n'est pas lu"
          />
        )}
      </div>

      {selected != null && (
        <div className="devis-list" style={{ marginTop: 18 }}>
          {traceLoading && <div className="devis-empty">Chargement de la traçabilité…</div>}
          {traceError && !traceLoading && <div className="devis-empty err">Erreur : {traceError}</div>}
          {trace && !traceLoading && (
            <div style={{ padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>Lot {trace.lot.name}</h3>
                <span style={{ opacity: .75 }}>
                  <ProductLink id={trace.lot.product?.id}>{trace.lot.product?.name}</ProductLink>
                </span>
                <span style={{ opacity: .75 }}>
                  reste {fmtQty(trace.lot.qty)} · péremption {fmtDate(trace.lot.expiration)}
                </span>
                {trace.clients.length > 0 && (
                  <button className="devis-refresh" style={{ marginLeft: 'auto' }} onClick={() => exportRappel(trace)}>
                    ⤓ Exporter les clients ({trace.clients.length})
                  </button>
                )}
              </div>

              <h4 style={{ margin: '18px 0 6px' }}>Reçu de</h4>
              {trace.receptions.length === 0 ? (
                <div style={{ opacity: .7 }}>Aucune réception tracée (lot créé hors flux de réception).</div>
              ) : (
                <table>
                  <thead><tr><th>Date</th><th>Fournisseur</th><th className="num">Qté</th><th>Réception</th><th>Origine</th></tr></thead>
                  <tbody>
                    {trace.receptions.map((r, i) => (
                      <tr key={i}>
                        <td>{fmtDate(r.date)}</td>
                        <td>{r.partner?.name ?? '—'}</td>
                        <td className="num">{fmtQty(r.qty)}</td>
                        <td className="mono"><PickingPdfLink id={r.picking_id} name={r.picking} /></td>
                        <td><OriginLink link={r.origin_link} label={r.origin} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h4 style={{ margin: '18px 0 6px' }}>Livré à — clients à contacter en cas de rappel</h4>
              {trace.livraisons.length === 0 ? (
                <div style={{ opacity: .7 }}>Aucune livraison : ce lot n'est jamais sorti du stock.</div>
              ) : (
                <table>
                  <thead><tr><th>Date</th><th>Client</th><th>Email</th><th>Téléphone</th><th className="num">Qté</th><th>Livraison</th><th>Commande</th></tr></thead>
                  <tbody>
                    {trace.livraisons.map((l, i) => (
                      <tr key={i}>
                        <td>{fmtDate(l.date)}</td>
                        <td>{l.partner?.name ?? '—'}</td>
                        <td>{l.partner?.email ?? <span style={{ opacity: .6 }}>—</span>}</td>
                        <td>{l.partner?.phone ?? <span style={{ opacity: .6 }}>—</span>}</td>
                        <td className="num">{fmtQty(l.qty)}</td>
                        <td className="mono"><PickingPdfLink id={l.picking_id} name={l.picking} /></td>
                        <td className="mono"><OriginLink link={l.origin_link} label={l.origin} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h4 style={{ margin: '18px 0 6px' }}>Stock restant</h4>
              {trace.stock.length === 0 ? (
                <div style={{ opacity: .7 }}>Plus de stock pour ce lot.</div>
              ) : (
                <table>
                  <thead><tr><th>Emplacement</th><th className="num">Quantité</th><th className="num">Réservé</th></tr></thead>
                  <tbody>
                    {trace.stock.map((s, i) => (
                      <tr key={i}>
                        <td>{s.location}</td>
                        <td className="num">{fmtQty(s.qty)}</td>
                        <td className="num">{s.reserved ? fmtQty(s.reserved) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
